// === INBOEDEL — form validation ===
// Pure validation extracted verbatim from InboedelScreen. validateInboedel returns a map of
// {key, params} i18n entries (the screen translates them); scrollToFirstError focuses the first
// errored field. `now` and the cross-product lock flags are injected so the rules are
// deterministic in tests. Mirrors personenauto/validation.ts.
import { readInboedelGlassAmount } from '../../../utils/glassPremium';
import { COVERAGE_AMOUNT_MAX, COVERAGE_AMOUNT_RECALC_MIN } from './coverages';

export interface InboedelValidationEntry {
  key: string;
  params?: Record<string, unknown>;
}

export interface ValidateInboedelOptions {
  now: Date;
  isAddressLocked: boolean;
  isBuildingDetailsLocked: boolean;
  isGezinsamenstellingLocked: boolean;
  /**
   * The rebuild-value input is read-only because Infofolio supplied the value
   * (`infofolioFilledFields.has('herbwrd')`). Suppresses the fat-finger band below: a value the
   * customer cannot edit must not raise an error telling them to edit it.
   *
   * REQUIRED, not optional, on purpose. There are two callers — the screen's handleNext gate and
   * useCoverageCalculation's debounced auto-calc gate — and an optional flag let the second one
   * be missed silently, which put the band error back on the disabled field and blocked
   * recalculation. Keeping it required means tsc names every call site.
   */
  isGlassRebuildValueLocked: boolean;
  /**
   * `inboedelGlassAllowed(...)` for this render. When false the Glas tile is hidden, GP never
   * reaches the wire and the herbouwwaarde has no control on screen, so no BA_HERBWRD error may
   * be raised (an error nothing renders is a dead "Volgende"). Required for the same reason as
   * isGlassRebuildValueLocked: both callers must say it.
   */
  glassAllowed: boolean;
}

export const INBOEDEL_INSURED_SUM_MAX = 175000;

/**
 * FAT-FINGER GUARDS for the Midglas rebuild value. Read this before changing or citing them.
 *
 * THESE NUMBERS WERE CHOSEN BY THE TEAM. They are not derived from any source. Nothing in this
 * repo bounds BA_HERBWRD: a search on 2026-08-17 of the product schemas (SAA + Frisia, both
 * Contractdocument and Relatiedocument), every `x-*` metadata key, all 362 recorded engine
 * responses in private/saa-responses, the BFF and Infofolio docs, and the next app's
 * declarative screen validation found no minimum or maximum anywhere.
 *
 * They are specifically NOT:
 *  - A TARIFF LIMIT. The glass tariff has a single band boundary at 500.000 (below it 30/yr,
 *    at or above it 39/yr) and is FLAT above that, so nothing prices differently anywhere
 *    inside this range.
 *  - AN ACCEPTANCE RANGE FROM THE ENGINE. The engine has never stated one. Its only observed
 *    rejection is an absent or zero value ("Herbouwwaarde is een verplicht veld voor Midglas
 *    Glas dekking"), which the `<= 0` branch below already covers. Live probes accepted every
 *    value tried between 250.000 and 900.000; nothing outside that was ever tried, so "no upper
 *    bound" means "none observed", not "proven unbounded".
 *  - A SCHEMA CONSTRAINT. BA_HERBWRD declares only the AFD format N..15, which is a digit
 *    count, in both the woonhuis and inboedel schemas.
 *
 * All they do is stop an obviously mistyped amount (1, or 99.999.999) reaching the wire. Do not
 * quote them to anyone as the accepted range. If the product square ever states a real one,
 * replace these and record it here.
 *
 * Deliberately NOT reused: COVERAGE_AMOUNT_MAX / COVERAGE_AMOUNT_RECALC_MIN (15.001-50.000)
 * bound the optional CONTENTS covers, and INBOEDEL_INSURED_SUM_MAX bounds the inboedelwaarde.
 * Neither is a building rebuild cost; 50.000 as a maximum would reject the 269.194 seen live.
 *
 * If you change them, update the amounts baked into
 * `screens.inboedel.validation.glassRebuildValueTooLow` / `...TooHigh` in nl.json + en.json;
 * the boundary tests pin the copy against these constants.
 */
export const GLASS_REBUILD_VALUE_MIN = 50000;
export const GLASS_REBUILD_VALUE_MAX = 5000000;

export function validateInboedel(data: any, opts: ValidateInboedelOptions): Record<string, InboedelValidationEntry> {
  const { isAddressLocked, isBuildingDetailsLocked, isGezinsamenstellingLocked } = opts;
  const errors: Record<string, InboedelValidationEntry> = {};
  const ba = data?.Contractdocument?.PP?.BA;
  const ip = data?.Contractdocument?.PP?.IP;
  const ib = data?.Contractdocument?.PP?.IB;

  // Postal Code validation with format check
  if (!ba?.BA_PCODE) {
    errors['Contractdocument.PP.BA.BA_PCODE'] = { key: 'screens.inboedel.validation.postalCodeRequired' };
  } else {
    const postalCodePattern = /^[0-9]{4}\s?[A-Z]{2}$/i;
    if (!postalCodePattern.test(ba.BA_PCODE)) {
      errors['Contractdocument.PP.BA.BA_PCODE'] = { key: 'screens.inboedel.validation.postalCodeInvalid' };
    }
  }

  // House Number validation
  if (!ba?.BA_HUISNR) {
    errors['Contractdocument.PP.BA.BA_HUISNR'] = { key: 'screens.inboedel.validation.houseNumberRequired' };
  }

  // Address validation - only require street/city if address is NOT locked from a previous screen
  // When address is locked (validated on Woonhuis), the lookup may still be in progress on this screen
  if (!isAddressLocked && (!ba?.BA_STRAAT || !ba?.BA_PLAATS)) {
    errors['Contractdocument.PP.BA.BA_STRAAT'] = { key: 'screens.inboedel.validation.addressNotValidated' };
  }

  // Building characteristics are only required when NOT reused from a sibling property
  // product (same building). When isBuildingDetailsLocked the values are copied in and the
  // inputs are hidden, so the user never re-enters them whatever the screen order is.
  if (!isBuildingDetailsLocked) {
    // House Type validation
    if (!ba?.BA_TYPEWON) {
      errors['Contractdocument.PP.BA.BA_TYPEWON'] = { key: 'screens.inboedel.validation.houseTypeRequired' };
    }

    // Property Use validation
    if (!ba?.BA_WONING) {
      errors['Contractdocument.PP.BA.BA_WONING'] = { key: 'screens.inboedel.validation.propertyUseRequired' };
    }

    // Surface Area validation
    if (!ba?.BA_OPPWON || ba.BA_OPPWON <= 0) {
      errors['Contractdocument.PP.BA.BA_OPPWON'] = { key: 'screens.inboedel.validation.surfaceAreaRequired' };
    }

    // Wall Type validation
    if (!ba?.BA_AARDBW) {
      errors['Contractdocument.PP.BA.BA_AARDBW'] = { key: 'screens.inboedel.validation.wallTypeRequired' };
    }

    // Roof Type validation
    if (!ba?.BA_AARDDAK) {
      errors['Contractdocument.PP.BA.BA_AARDDAK'] = { key: 'screens.inboedel.validation.roofTypeRequired' };
    }

    // R515 Rhion-specific field validations
    // Own home (Eigen woning)
    if (!ba?.BA_EIGENWO) {
      errors['Contractdocument.PP.BA.BA_EIGENWO'] = { key: 'screens.inboedel.validation.ownHomeRequired' };
    }

    // Permanent resident (Permanent bewoner)
    if (!ba?.BA_PERMBEW) {
      errors['Contractdocument.PP.BA.BA_PERMBEW'] = { key: 'screens.inboedel.validation.permanentResidentRequired' };
    }

    // Adjacent building type (Belendend gebouw)
    if (!ba?.BA_BDRFBEL) {
      errors['Contractdocument.PP.BA.BA_BDRFBEL'] = { key: 'screens.inboedel.validation.adjacentBuildingRequired' };
    }
  }

  // Construction year (Bouwjaar) - only when not locked from Woonhuis
  if (!isBuildingDetailsLocked) {
    if (!ba?.BA_BOUWJR) {
      errors['Contractdocument.PP.BA.BA_BOUWJR'] = { key: 'screens.woonhuis.validation.constructionYearRequired' };
    } else {
      const currentYear = opts.now.getFullYear();
      const year = Number(ba.BA_BOUWJR);
      if (isNaN(year) || year < 1800 || year > currentYear) {
        errors['Contractdocument.PP.BA.BA_BOUWJR'] = { key: 'screens.woonhuis.validation.constructionYearInvalid', params: { maxYear: currentYear } };
      }
    }
  }

  // Main coverage validation
  // Only validate IP_GSS if not locked (if locked, it's managed from Woonhuis)
  if (!isGezinsamenstellingLocked && !ip?.IP_GSS) {
    errors['Contractdocument.PP.IP.IP_GSS'] = { key: 'screens.inboedel.validation.familyCompositionRequired' };
  }
  if (!ip?.IP_VERZSOM || ip?.IP_VERZSOM <= 0) {
    errors['Contractdocument.PP.IP.IP_VERZSOM'] = { key: 'screens.inboedel.validation.insuredSumRequired' };
  } else if (ip?.IP_VERZSOM > INBOEDEL_INSURED_SUM_MAX) {
    errors['Contractdocument.PP.IP.IP_VERZSOM'] = { key: 'screens.inboedel.validation.insuredSumTooHigh' };
  }
  // IP_ERB is always 100 (All Risk) - no validation needed
  // IP_GARONDV is derived from IB_INBVST - no validation needed
  if (!ib?.IB_INBVST) {
    errors['Contractdocument.PP.IB.IB_INBVST'] = { key: 'screens.inboedel.validation.contentsValueDeterminationRequired' };
  }

  // Vaste taxatie: end date + VRT answer are required while 'V' is selected
  if (ib?.IB_INBVST === 'V') {
    if (!data?.Contractdocument?.PP?.BA?.BA_EINDGTX) {
      errors['Contractdocument.PP.BA.BA_EINDGTX'] = { key: 'screens.woonhuis.validation.valuationEndDateRequired' };
    }
    if (!data?.Supportsection?.Divers?.TaxatieVRT) {
      errors['Supportsection.Divers.TaxatieVRT'] = { key: 'screens.woonhuis.validation.vrtValuationRequired' };
    }
  }

  const dekkingen = data?.Supportsection?.Dekkingen || {};
  // Only while the Glas tile is on screen (glassAllowed); its error renders on that tile.
  if (dekkingen.Glas === 'J' && opts.glassAllowed) {
    const herbwrd = readInboedelGlassAmount(data);
    if (herbwrd <= 0) {
      // Applies even when locked: the engine rejects an absent value outright, and a locked but
      // EMPTY field means the Infofolio value got lost, which the customer resolves by switching
      // Glas off or re-checking the address, not by typing here (there is no input).
      errors['Contractdocument.PP.BA.BA_HERBWRD'] = { key: 'screens.inboedel.validation.glassRebuildValueRequired' };
    } else if (!opts.isGlassRebuildValueLocked) {
      // Band only guards what a HUMAN typed. When Infofolio supplied the value the input is
      // read-only (InboedelScreen: disabled={infofolioFilledFields.has('herbwrd')}), so raising
      // "vul een bedrag van minimaal X in" would be an error the customer cannot act on — the
      // only escape would be switching Glas off, which is not what the message says. An
      // out-of-band Infofolio value goes to the engine and is rated or declined on its merits.
      if (herbwrd < GLASS_REBUILD_VALUE_MIN) {
        errors['Contractdocument.PP.BA.BA_HERBWRD'] = { key: 'screens.inboedel.validation.glassRebuildValueTooLow' };
      } else if (herbwrd > GLASS_REBUILD_VALUE_MAX) {
        errors['Contractdocument.PP.BA.BA_HERBWRD'] = { key: 'screens.inboedel.validation.glassRebuildValueTooHigh' };
      }
    }
  }
  if (dekkingen.Lijfsieraden === 'J') {
    const lsVerzsom = data?.Contractdocument?.PP?.LS?.LS_VERZSOM || 0;
    if (lsVerzsom > 0 && lsVerzsom < COVERAGE_AMOUNT_RECALC_MIN) errors['Contractdocument.PP.LS.LS_VERZSOM'] = { key: 'screens.inboedel.validation.jewelryMinAmount' };
    if (lsVerzsom > COVERAGE_AMOUNT_MAX) errors['Contractdocument.PP.LS.LS_VERZSOM'] = { key: 'screens.inboedel.validation.jewelryMaxAmount' };
  }
  if (dekkingen.Eigenaarsbelang === 'J') {
    const opVerzsom = data?.Contractdocument?.PP?.OP?.OP_VERZSOM || 0;
    if (opVerzsom > 0 && opVerzsom < COVERAGE_AMOUNT_RECALC_MIN) errors['Contractdocument.PP.OP.OP_VERZSOM'] = { key: 'screens.inboedel.validation.genericMinAmount' };
    if (opVerzsom > COVERAGE_AMOUNT_MAX) errors['Contractdocument.PP.OP.OP_VERZSOM'] = { key: 'screens.inboedel.validation.genericMaxAmount' };
  }
  if (dekkingen.Huurdersbelang === 'J') {
    const huVerzsom = data?.Contractdocument?.PP?.HU?.HU_VERZSOM || 0;
    if (huVerzsom > 0 && huVerzsom < COVERAGE_AMOUNT_RECALC_MIN) errors['Contractdocument.PP.HU.HU_VERZSOM'] = { key: 'screens.inboedel.validation.genericMinAmount' };
    if (huVerzsom > COVERAGE_AMOUNT_MAX) errors['Contractdocument.PP.HU.HU_VERZSOM'] = { key: 'screens.inboedel.validation.genericMaxAmount' };
  }
  if (dekkingen.Bijzonderebezittingen === 'J') {
    const kbVerzsom = data?.Contractdocument?.PP?.KB?.KB_VERZSOM || 0;
    if (kbVerzsom > 0 && kbVerzsom < COVERAGE_AMOUNT_RECALC_MIN) errors['Contractdocument.PP.KB.KB_VERZSOM'] = { key: 'screens.inboedel.validation.specialMinAmount' };
    if (kbVerzsom > COVERAGE_AMOUNT_MAX) errors['Contractdocument.PP.KB.KB_VERZSOM'] = { key: 'screens.inboedel.validation.specialMaxAmount' };
  }

  return errors;
}

// Mapping of validation error keys to form element IDs
export const INBOEDEL_ERROR_FIELD_IDS: Record<string, string> = {
  'Contractdocument.PP.BA.BA_PCODE': 'postcode',
  'Contractdocument.PP.BA.BA_HUISNR': 'huisnummer',
  'Contractdocument.PP.BA.BA_TOEVOEG': 'toevoeging',
  'Contractdocument.PP.BA.BA_STRAAT': 'straat',
  'Contractdocument.PP.BA.BA_PLAATS': 'plaats',
  'Contractdocument.PP.BA.BA_TYPEWON': 'typewon',
  'Contractdocument.PP.BA.BA_WONING': 'woning',
  'Contractdocument.PP.BA.BA_OPPWON': 'oppervlakte',
  'Contractdocument.PP.BA.BA_AARDBW': 'aardbw',
  'Contractdocument.PP.BA.BA_AARDDAK': 'aarddak',
  'Contractdocument.PP.BA.BA_EIGENWO': 'eigenwo',
  'Contractdocument.PP.BA.BA_PERMBEW': 'permbew',
  'Contractdocument.PP.BA.BA_OPZPKVA': 'opzpkva',
  'Contractdocument.PP.BA.BA_BDRFBEL': 'bdrfbel',
  'Contractdocument.PP.BA.BA_BOUWJR': 'bouwjaar',
  'Contractdocument.PP.IP.IP_GSS': 'gezin',
  'Contractdocument.PP.IP.IP_VERZSOM': 'verzsom',
  'Contractdocument.PP.IP.IP_ERB': 'erb',
  'Contractdocument.PP.IP.IP_GARONDV': 'garondv',
  'Contractdocument.PP.IB.IB_INBVST': 'inbvst',
  'Contractdocument.PP.BA.BA_EINDGTX': 'eindgtx',
  'Supportsection.Divers.TaxatieVRT': 'taxvrt',
  'Contractdocument.PP.LS.LS_VERZSOM': 'jewelry-amount',
  'Contractdocument.PP.BI.BI_VERZSOM': 'outside-home-amount',
  'Contractdocument.PP.HU.HU_VERZSOM': 'owner-tenant-amount',
  'Contractdocument.PP.KB.KB_VERZSOM': 'special-possessions-amount',
  'Contractdocument.PP.BA.BA_HERBWRD': 'glass-rebuild-value',
};

// Scroll to the first field with a validation error
export function scrollToFirstError(errors: Record<string, string>): void {
  const errorKeys = Object.keys(errors);
  if (errorKeys.length === 0) return;

  // Find the first error field that has a corresponding element ID
  for (const errorKey of errorKeys) {
    const elementId = INBOEDEL_ERROR_FIELD_IDS[errorKey];
    if (elementId) {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
        return;
      }
    }
  }

  // Fallback to scrolling to top if no element found
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
